import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import {
  View,
  Platform,
  LayoutRectangle,
  useWindowDimensions,
  StyleSheet,
} from 'react-native';
import { Image } from 'expo-image';
import Animated, { useAnimatedReaction, useAnimatedStyle, useSharedValue, withTiming } from 'react-native-reanimated';
import { GestureDetector, GestureHandlerRootView } from 'react-native-gesture-handler';
import { SystemBars } from 'react-native-edge-to-edge';
import { router, usePathname } from 'expo-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useCommunity } from '@/hooks/useCommunity';
import { getMemberPath } from '@/constants/routes';

import { useIsLargeScreen } from '@/hooks/useIsLargeScreen';
import { Photo } from '@/models/Photo';
import { ImageViewerHeader } from '@/components/ui/image-viewer/ImageViewerHeader';
import { ImageViewerBottomSheet } from '@/components/ui/image-viewer/ImageViewerBottomSheet';
import { HStack } from '@/components/gluestack/hstack';
import { CarouselSideArrows } from '@/components/ui/image-viewer/CarouselSideArrows';
import LikesCommentsOverlay from '@/components/domain/photos/LikesCommentsOverlay';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { ImageViewerContent } from '@/components/ui/image-viewer/ImageViewerContent';
import { ImageViewerCarousel } from '@/components/ui/image-viewer/ImageViewerCarousel';
import { ImageViewerBackground } from '@/components/ui/image-viewer/ImageViewerBackground';
import { ImageViewerContentLocked } from '@/components/ui/image-viewer/ImageViewerContentLocked';
import { useImageViewerGestures } from '@/components/ui/image-viewer/useImageViewerGestures';
import { useImageViewerAnimation } from '@/components/ui/image-viewer/useImageViewerAnimation';
import { useImageViewerSharedValues } from '@/components/ui/image-viewer/useImageViewerSharedValues';
import { usePaneSlots } from '@/components/ui/image-viewer/usePaneSlots';
import { resetPaneTransforms } from '@/components/ui/image-viewer/ImageViewerHelpers';
import { TRACK_SNAP_TIMING } from '@/components/ui/image-viewer/ImageViewerConstants';
import { hasUriDisplayed } from '@/components/ui/image-viewer/imageDimensionsCache';
import { scheduleOnRN } from 'react-native-worklets';
import {
  Modal,
  ModalBackdrop,
  ModalContent,
} from '@/components/gluestack/modal';
import { BottomSheetModalProvider } from '@gorhom/bottom-sheet';
import { BlurTargetView } from 'expo-blur';
import { PhotosService } from '~/services/domain/PhotosService';
import { MessageView } from '@/components/ui/MessageView';
import { ButtonWrapper } from '@/components/ui/ButtonWrapper';
import { Alert02Icon } from '@hugeicons-pro/core-stroke-rounded';
import { SafeBlurView } from '@/components/ui/SafeBlurView';

// Bounded prefetch ring buffer size — keep enough for snappy adjacent navigation
// without pinning every browsed full-size image in memory. With the 3-pane
// carousel the immediate neighbors (±1) are already mounted and decoded by the
// pane <Image>s themselves, so we only prefetch the next-after-next (±2): when
// the user navigates one step, the rotating-slot model rebinds the new far-side
// pane to activeIndex ± 2, and we want that decode to be a disk-cache hit.
const PREFETCH_RING_SIZE = 4;

interface IImageViewerProps {
  visible: boolean;
  uri: string | null;
  photo?: Photo | null;
  photos?: Photo[] | undefined;
  thumbnailLayout?: LayoutRectangle | null;
  onClose: () => void;
  disableHistoryNavigation?: boolean;
  isClosingDueToNavigation?: boolean;
  allowImageDownload?: boolean;
  /**
   * Suppresses the photo's social actions (likes/comments overlay, caption
   * sheet, and context menu). The owner info chip (avatar, username, date) is
   * still shown when the photo carries a `Member`. Use for images not backed by
   * a Photo record but with a known sender — e.g. conversation attachments.
   */
  disableSocialActions?: boolean;
  // When the provider fails to fetch the photo details
  // the viewer renders a centered MessageView instead of a blank chrome.
  loadError?: unknown;
}



export const ImageViewer: React.FC<IImageViewerProps> = ({
  visible,
  uri,
  photo,
  photos,
  thumbnailLayout,
  onClose,
  disableHistoryNavigation = false,
  isClosingDueToNavigation = false,
  allowImageDownload = false,
  disableSocialActions = false,
  loadError = null,
}) => {
  const [isClosing, setIsClosing] = useState(false);
  const [uiVisible, setUiVisible] = useState(true);
  const [isBottomSheetOpen, setIsBottomSheetOpen] = useState(false);
  const chromeVisible = uiVisible && !isBottomSheetOpen;
  const isLargeScreen = useIsLargeScreen();
  const { t } = useTranslation();
  const community = useCommunity();
  // The close-start chrome hide is only needed on Android: there the dark-tinted blurs
  // sample (via dimezisBlurView) the content behind them as the backdrop fades, causing a
  // brief blur flash on close (in both light and dark mode). iOS samples the real layers
  // (no flash) and abruptly hiding the chrome there reads as a blink — so keep the original
  // behavior there (chrome hides when the close completes).
  const hideChromeOnCloseStart = Platform.OS === 'android';
  const isClosingProgrammatically = useRef(false);
  const hasPushedHistoryRef = useRef(false);
  const closeViewerRef = useRef<() => void>(() => {});
  const blurTargetRef = useRef<View | null>(null);
  const insets = useSafeAreaInsets();
  const screen = useWindowDimensions();
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const [activeUri, setActiveUri] = useState<string | null>(uri);
  const [activePhoto, setActivePhoto] = useState<Photo | null>(photo ?? null);
  const [imageLoadError, setImageLoadError] = useState<{ uri: string; message?: string } | null>(null);
  // Photo index whose URI should currently feed the (blurred) background. This
  // is computed from trackTranslateX via a worklet-side animated reaction —
  // it flips to the next/prev photo as soon as the carousel crosses the 50%
  // mark (mid-drag, during the snap after finger-release, or during the snap
  // triggered by an arrow press), so the background cross-dissolve starts
  // immediately rather than waiting for the post-snap commit. Stays equal to
  // activeIndex during steady-state browsing.
  const [displayedBgIndex, setDisplayedBgIndex] = useState<number>(0);
  // Per-slot photo index. Each entry is the absolute photo index that the
  // corresponding slot (0/1/2) is currently rendering. Initialized so slot 1
  // holds the active photo and slots 0/2 hold its neighbors. After each
  // navigation commit, exactly ONE slot rotates its pageIndex to the new
  // far-side neighbor — the slot at center keeps the same URI and the same
  // transform position, so there is zero pixel change at center across the
  // commit (no flicker). Out-of-range values (-1 or photos.length) render null.
  const [slotPageIndices, setSlotPageIndices] = useState<[number, number, number]>([-1, 0, 1]);
  // Which slot index (0/1/2) is currently visible at center. Mirrors
  // paneSlots.activeSlot (the SV worklets read for pinch / tap targeting).
  const [activeSlotIndex, setActiveSlotIndex] = useState<0 | 1 | 2>(1);
  // Bounded ring buffer to avoid pinning every browsed full-size image in memory
  const prefetchedUrlsRef = useRef<string[]>([]);

  // Subscribe to React Query photo detail cache to reflect live updates (likes/comments)
  // Include IsLocked in the cache key since the same photo may be locked/unlocked in different contexts
  const photoId = (activePhoto ?? photo)?.Id;
  const photoIsLocked = (activePhoto ?? photo)?.IsLocked ?? false;
  const { data: cachedPhoto } = useQuery({
    queryKey: ['photo', photoId, { locked: photoIsLocked }],
    enabled: false,
    queryFn: () => PhotosService.getPhotoDetails(photoId as number),
    initialData: activePhoto ?? photo ?? undefined,
    staleTime: Infinity
  });
  // Non-Photo images (disableSocialActions) have no real id, so they all share
  // the photoId=0 cache key above — reading cachedPhoto would pin the chip to
  // the first opened photo after a swipe (or collide with a real cached photo).
  // Use the per-photo activePhoto directly for them; the cache layer only adds
  // value for real photos (live like/comment updates).
  const resolvedPhoto = useMemo(
    () =>
      disableSocialActions
        ? activePhoto ?? photo ?? null
        : cachedPhoto ?? activePhoto ?? photo ?? null,
    [disableSocialActions, cachedPhoto, activePhoto, photo]
  );

  // For non-Photo images (disableSocialActions) ownership is per-photo on
  // resolvedPhoto (= activePhoto); the photo/cachedPhoto fallbacks share the
  // photoId=0 cache key and would leak the first photo's ownership (download
  // affordance) onto later photos after a swipe.
  const isOwnerForViewerDownload = disableSocialActions
    ? resolvedPhoto?.IsOwner === true
    : resolvedPhoto?.IsOwner === true ||
      photo?.IsOwner === true ||
      activePhoto?.IsOwner === true ||
      cachedPhoto?.IsOwner === true;

  const viewerAllowsImageDownload =
    allowImageDownload === true || isOwnerForViewerDownload;

  const isPhotoLocked = resolvedPhoto?.IsLocked ?? false;
  const viewerLoadErrorMessage = useMemo(() => {
    if (loadError) {
      return loadError instanceof Error ? loadError.message : String(loadError);
    }

    if (activeUri && imageLoadError?.uri === activeUri) {
      return imageLoadError.message || t('notFound.title');
    }

    return null;
  }, [activeUri, imageLoadError, loadError, t]);

  const isDesktop = Platform.OS === 'web' && isLargeScreen;
  const pathname = usePathname();

  // Pane dimensions for the carousel. We measure the actual viewer container
  // (the parent View that also hosts the side prev/next arrows) via onLayout
  // and use those dimensions, with useWindowDimensions as a fallback for the
  // first frame before layout has been measured. This guarantees the image's
  // vertical center matches the arrow icons' vertical center on every Android
  // configuration, regardless of whether `useWindowDimensions` reports the
  // full physical screen height or only the content area below the status bar
  // (which varies by device / OS version even with edge-to-edge enabled).
  const [containerSize, setContainerSize] = useState<{ width: number; height: number } | null>(null);
  const handleContainerLayout = useCallback((e: { nativeEvent: { layout: { width: number; height: number } } }) => {
    const { width, height } = e.nativeEvent.layout;
    setContainerSize((prev) => {
      if (prev && prev.width === width && prev.height === height) return prev;
      return { width, height };
    });
  }, []);
  const paneWidth = containerSize?.width ?? screen.width;
  const paneHeight = containerSize?.height ?? screen.height;

  // Single bag of viewer-level reanimated shared values + per-pane SV bag pool.
  const sv = useImageViewerSharedValues();
  const paneSlots = usePaneSlots();
  const bgOpacityStyle = useAnimatedStyle(() => ({ opacity: sv.bgOpacity.value }));
  // Mirrors the last displayedBgIndex emitted by the worklet reaction below so
  // we only schedule a JS-side setState when the index actually changes.
  // Declared up here (next to the rest of the viewer-level SVs) even though
  // it's only consumed below — keeps all SV declarations co-located.
  const lastBgIndexShared = useSharedValue<number>(0);

  const toggleUiVisibility = () => {
    setUiVisible((prev) => {
      const newUiVisible = !prev;
      SystemBars.setHidden(!newUiVisible);
      return newUiVisible;
    });
  };

  // When the viewer is opened without a `photos` array (e.g. the /photo/:guid
  // deep-link route, or the PostCard / ProfileScreen single-photo openers
  // which only have a URI + photoId up front), fall back to a single-element
  // array so the carousel renders the active photo. With one photo,
  // navigation is naturally disabled (canNavigatePrev/Next both false).
  //
  // Crucially for the PostCard / ProfileScreen flow: those triggers pass a
  // URI but no Photo object — the Photo is fetched async and arrives later.
  // If we left `effectivePhotos` undefined during that gap, the carousel
  // pane would render no <Image> at all, and the open-morph fade-out would
  // hand off to a black gap (the morph fades to opacity 0 on a fixed
  // schedule, regardless of whether anything is mounted underneath).
  // Synthesizing a Photo whose FullSizeUrl tracks `activeUri` gives the pane
  // a URI to render from frame 0 — and since the morph already loaded that
  // URI into expo-image's cache, the pane decode is instant. The
  // activeUri-prefetch effect below then upgrades activeUri to the resolved
  // photo's full-size URL only AFTER prefetching, so the swap stays
  // gap-free even when the trigger URI is a smaller thumbnail.
  const effectivePhotos = useMemo<Photo[] | undefined>(() => {
    if (photos && photos.length > 0) return photos;
    if (activeUri) {
      // Prefer the resolved Photo if available so its Id (used by the
      // visible-init findIndex) matches when carousel mode kicks in later;
      // but always force FullSizeUrl to track activeUri so the pane URI
      // never drifts out of sync with the morph.
      const base = photo ?? new Photo();
      // Force FullSizeUrl to track activeUri (so the pane URI doesn't drift
      // from the morph's). Also ensure IconUrl falls back to activeUri when
      // the resolved Photo isn't loaded yet — the default `Photo` class
      // initializes IconUrl to an empty string, and `'' ?? activeUri`
      // returns '' (not nullish), which would short-circuit the
      // carouselBgUri fallback chain and leave the blurred background with
      // an empty source until the async getPhotoDetails fetch resolves.
      const synthesized = Object.assign(
        Object.create(Object.getPrototypeOf(base)),
        base,
        {
          FullSizeUrl: activeUri,
          IconUrl: base.IconUrl || activeUri,
        }
      ) as Photo;
      return [synthesized];
    }
    if (photo) return [photo];
    return undefined;
  }, [photos, photo, activeUri]);

  // Background URI for the carousel browsing state. Tracks displayedBgIndex
  // (driven from trackTranslateX by the worklet reaction below) so the blurred
  // background starts cross-dissolving as soon as the user has swept past the
  // half-way point on a swipe — or, equivalently, as soon as a snap animation
  // (from finger-release or arrow press) crosses 50%. Falls back to activeUri
  // while photos isn't yet known.
  //
  // We deliberately prefer IconUrl (the small thumbnail) over FullSizeUrl: the
  // <ImageViewerBackground> uses CSS blur on iOS/web only (Android: solid backdrop). Icon URL sizing:
  // resolution is irrelevant — a 200px thumbnail looks identical to a 2000px
  // full-size when blurred that hard, but costs ~1% of the bytes/decode time.
  // This matters a lot on first open over a slow network. FullSizeUrl is only
  // used as a last-resort fallback if the photo somehow lacks an IconUrl.
  const carouselBgPhoto = useMemo(() => {
    if (!effectivePhotos || effectivePhotos.length === 0) return null;
    const safeIdx = Math.max(0, Math.min(effectivePhotos.length - 1, displayedBgIndex));
    return effectivePhotos[safeIdx] ?? null;
  }, [effectivePhotos, displayedBgIndex]);

  // Latched background URI per displayed photo index for the lifetime of this
  // open session. Without latching, opening from a trigger that only knows a
  // photoId (e.g. PostCard non-compact, profile picture) would render the
  // background with `activeUri` (FullSizeUrl) on the first frame, then SWITCH
  // to `IconUrl` once the async PhotosService.getPhotoDetails fetch resolves
  // and updatePhoto fires. Because <ImageViewerBackground>'s expo-image is
  // configured with a 200ms cross-dissolve transition, that URI swap shows
  // up as a visible flash on open. Latching keeps whichever URI we picked
  // first for the currently-displayed photo (still preferring IconUrl when
  // the Photo object is known up front, FullSizeUrl as a one-shot fallback
  // otherwise) and only resolves a new URI when the carousel navigates to a
  // different index. The cache is cleared on every fresh open and on close
  // (see the visible-init effect below + resetState).
  const bgUriByIndexRef = useRef<Map<number, string>>(new Map());
  const carouselBgUri = useMemo(() => {
    const cached = bgUriByIndexRef.current.get(displayedBgIndex);
    if (cached) return cached;
    const candidate = carouselBgPhoto?.IconUrl ?? carouselBgPhoto?.FullSizeUrl ?? activeUri;
    if (candidate) bgUriByIndexRef.current.set(displayedBgIndex, candidate);
    return candidate;
  }, [carouselBgPhoto, displayedBgIndex, activeUri]);

  // When the active photo is locked the entire viewer chrome switches to the
  // locked-content path (no carousel), so we keep the background tied to the
  // locked photo's icon URL rather than the (possibly mid-snap) carousel bg.
  const backgroundUri = isPhotoLocked ? resolvedPhoto?.IconUrl : carouselBgUri;

  // Initialize carousel state on open. We seed slot 1 with the active photo
  // and slots 0/2 with its neighbors, then snap trackTranslateX to the
  // resting position for the active index.
  useEffect(() => {
    if (!visible) return;
    bgUriByIndexRef.current.clear();
    setImageLoadError(null);
    setActiveUri(uri);
    let idx = 0;
    if (effectivePhotos) {
      // Prefer matching the resolved Photo by Id; fall back to a URI match for
      // openers that pass only a uri + carousel and no Photo record (e.g.
      // conversation photos, which have no Photo id). Without the fallback the
      // carousel always opened on the first item instead of the tapped one.
      const found = photo
        ? effectivePhotos.findIndex((p) => p.Id === photo.Id)
        : effectivePhotos.findIndex((p) => p.FullSizeUrl === uri);
      idx = found >= 0 ? found : 0;
    }
    // Seed the active photo from the opened carousel entry for openers that pass
    // a non-Photo carousel without a Photo object (disableSocialActions, e.g.
    // conversation photos), so the header shows the opened photo's info (sender,
    // date) immediately instead of only after the first swipe. Photo-backed
    // openers keep their async-resolved photo and are unaffected.
    setActivePhoto(photo ?? (disableSocialActions && photos ? photos[idx] ?? null : null));
    setActiveIndex(idx);
    setDisplayedBgIndex(idx);
    lastBgIndexShared.value = idx;
    setSlotPageIndices([idx - 1, idx, idx + 1]);
    setActiveSlotIndex(1);
    paneSlots.activeSlot.value = 1;
    const rest = -idx * paneWidth;
    sv.trackTranslateX.value = rest;
    sv.lastTrackTranslateX.value = rest;
  }, [visible]);

  // Drive displayedBgIndex from trackTranslateX. Math.round() flips at the 50%
  // mark in either direction, so the cross-dissolve starts exactly when the
  // next photo is more than half-visible — covering both gesture drags AND the
  // snap animation that runs after finger release / arrow press. The
  // lastBgIndexShared SV (declared up with the other SVs) lets us only
  // schedule a JS update when the index actually changes.
  const bgPhotoCount = effectivePhotos?.length ?? 0;
  useAnimatedReaction(
    () => sv.trackTranslateX.value,
    (current) => {
      'worklet';
      if (paneWidth <= 0 || bgPhotoCount <= 0) return;
      const raw = -current / paneWidth;
      let idx = Math.round(raw);
      if (idx < 0) idx = 0;
      else if (idx > bgPhotoCount - 1) idx = bgPhotoCount - 1;
      if (idx !== lastBgIndexShared.value) {
        lastBgIndexShared.value = idx;
        scheduleOnRN(setDisplayedBgIndex, idx);
      }
    },
    [paneWidth, bgPhotoCount]
  );

  // Re-sync the carousel track on pane-width changes (rotation). We use a ref
  // to detect actual width changes vs. unrelated re-renders so we don't
  // clobber trackTranslateX during an in-progress snap animation.
  const prevPaneWidthRef = useRef<number>(paneWidth);
  useEffect(() => {
    if (paneWidth === prevPaneWidthRef.current) return;
    prevPaneWidthRef.current = paneWidth;
    if (!visible) return;
    const rest = -activeIndex * paneWidth;
    sv.trackTranslateX.value = rest;
    sv.lastTrackTranslateX.value = rest;
  }, [paneWidth, activeIndex, visible, sv]);

  // Nav helpers
  const photoCount = effectivePhotos?.length ?? 0;
  const canNavigatePrev = !!effectivePhotos && activeIndex > 0;
  const canNavigateNext = !!effectivePhotos && photoCount > 0 && activeIndex < photoCount - 1;
  const isCarouselEnabled = !!effectivePhotos && photoCount > 1;

  // Update the document title / web URL for the new active photo without
  // pushing a history entry.
  const updateWebHistoryForPhoto = useCallback((next: Photo) => {
    if (Platform.OS !== 'web' || typeof window === 'undefined') return;
    const guid = (next as any)?.Guid;
    if (!guid) return;
    try {
      window.history.replaceState({ imageViewer: true }, '', `/photo/${guid}`);
      const appName = community?.name || 'Grokio';
      const photoTitle = t('photos.photoOf', 'Photo of {{userName}}', {
        userName: next.Member.UserName,
      });
      document.title = `${appName} - ${photoTitle}`;
    } catch {
      // no-op
    }
  }, [community, t]);

  // Atomic commit after a swipe snap or arrow press. The rotating-slot model
  // means we DON'T touch trackTranslateX here — the snap animation already
  // left it at -(activeIndex + direction) * paneWidth, which is exactly the
  // resting position for the new active index. Instead we:
  //   1. Bump activeIndex.
  //   2. Bump activeSlotIndex (and the matching SV) to whichever slot now sits
  //      at center.
  //   3. Rotate ONE slot's pageIndex to the new far-side neighbor (the slot
  //      whose pageIndex is now obsolete — i.e., the one farthest from the
  //      new active in the opposite of the navigation direction).
  // Because steps 1-3 all happen in one React commit and no SV writes are
  // needed, the visible center pixel doesn't change across the commit and the
  // off-screen slot's URI/position swap is invisible.
  const commitShift = useCallback((direction: -1 | 1) => {
    if (!effectivePhotos) return;
    const nextIndex = activeIndex + direction;
    if (nextIndex < 0 || nextIndex >= effectivePhotos.length) return;
    const nextPhoto = effectivePhotos[nextIndex];

    // Rotate the slot whose pageIndex is now obsolete (smallest for forward,
    // largest for backward) to the new far-side neighbor.
    let staleSlot: 0 | 1 | 2 = 0;
    let newPageIndex = 0;
    if (direction === 1) {
      // Forward: the slot with the smallest pageIndex moves to active+1.
      let minVal = slotPageIndices[0];
      staleSlot = 0;
      for (let i = 1; i < 3; i++) {
        if (slotPageIndices[i] < minVal) {
          minVal = slotPageIndices[i];
          staleSlot = i as 0 | 1 | 2;
        }
      }
      newPageIndex = nextIndex + 1;
    } else {
      // Backward: the slot with the largest pageIndex moves to active-1.
      let maxVal = slotPageIndices[0];
      staleSlot = 0;
      for (let i = 1; i < 3; i++) {
        if (slotPageIndices[i] > maxVal) {
          maxVal = slotPageIndices[i];
          staleSlot = i as 0 | 1 | 2;
        }
      }
      newPageIndex = nextIndex - 1;
    }

    // The new active slot is whichever slot already holds the new active
    // pageIndex (which won't be the stale slot we're rotating).
    let newActiveSlot: 0 | 1 | 2 = 1;
    for (let i = 0; i < 3; i++) {
      if (slotPageIndices[i] === nextIndex) {
        newActiveSlot = i as 0 | 1 | 2;
        break;
      }
    }

    const nextSlots: [number, number, number] = [
      slotPageIndices[0],
      slotPageIndices[1],
      slotPageIndices[2],
    ];
    nextSlots[staleSlot] = newPageIndex;

    // Reset zoom on the previously-active pane and on the rotated pane (which
    // just got assigned a new photo). The new active pane should already be
    // at default 1x but reset for hygiene.
    resetPaneTransforms(paneSlots.slots[activeSlotIndex]);
    resetPaneTransforms(paneSlots.slots[staleSlot]);

    // Update the active-slot SV so worklets (pinch / tap) target the right
    // pane immediately. Direct JS-thread writes to a SharedValue are supported
    // and synchronized to the UI thread by Reanimated.
    paneSlots.activeSlot.value = newActiveSlot;

    setActiveIndex(nextIndex);
    setActiveSlotIndex(newActiveSlot);
    setSlotPageIndices(nextSlots);
    setActivePhoto(nextPhoto);
    setActiveUri(nextPhoto.FullSizeUrl);
    updateWebHistoryForPhoto(nextPhoto);
  }, [effectivePhotos, activeIndex, slotPageIndices, activeSlotIndex, paneSlots, updateWebHistoryForPhoto]);

  // Programmatic prev/next (side arrows + keyboard arrows). Animates the same
  // track-snap as a swipe before committing, so the visual transition matches.
  // We use the withTiming callback (not setTimeout) so that if the user
  // interrupts the snap with a gesture (which calls cancelAnimation on
  // trackTranslateX), the commit doesn't fire on stale state.
  const programmaticShiftInFlightRef = useRef<boolean>(false);
  const commitShiftRef = useRef(commitShift);
  useEffect(() => {
    commitShiftRef.current = commitShift;
  }, [commitShift]);

  const callProgrammaticCommit = useCallback((direction: -1 | 1) => {
    commitShiftRef.current(direction);
    programmaticShiftInFlightRef.current = false;
  }, []);
  const callClearInflight = useCallback(() => {
    programmaticShiftInFlightRef.current = false;
  }, []);

  const animateAndCommit = useCallback((direction: -1 | 1) => {
    if (!effectivePhotos) return;
    if (programmaticShiftInFlightRef.current) return;
    const nextIndex = activeIndex + direction;
    if (nextIndex < 0 || nextIndex >= effectivePhotos.length) return;
    programmaticShiftInFlightRef.current = true;
    const target = -nextIndex * paneWidth;
    sv.trackTranslateX.value = withTiming(target, TRACK_SNAP_TIMING, (finished) => {
      'worklet';
      if (finished) {
        scheduleOnRN(callProgrammaticCommit, direction);
      } else {
        scheduleOnRN(callClearInflight);
      }
    });
  }, [paneWidth, sv, effectivePhotos, activeIndex, callProgrammaticCommit, callClearInflight]);

  const goPrev = useCallback(() => {
    if (!canNavigatePrev) return;
    animateAndCommit(-1);
  }, [canNavigatePrev, animateAndCommit]);

  const goNext = useCallback(() => {
    if (!canNavigateNext) return;
    animateAndCommit(1);
  }, [canNavigateNext, animateAndCommit]);

  const handleImageLoadError = useCallback((failedUri: string, message?: string) => {
    setImageLoadError({ uri: failedUri, message });
  }, []);

  // Imperative neighbor (±2) prefetch into the disk cache. Deliberately
  // side-effect-only (NO setState): triggering it from the pane's load callback
  // must not re-render, because a re-render recreates the pane <Image> source
  // and re-fires onLoad — which would loop straight back here (Maximum update
  // depth). Reads the live carousel position via refs so the callback is stable.
  const activeUriRef = useRef<string | null>(activeUri);
  activeUriRef.current = activeUri;
  const activeIndexRef = useRef(activeIndex);
  activeIndexRef.current = activeIndex;
  const effectivePhotosRef = useRef(effectivePhotos);
  effectivePhotosRef.current = effectivePhotos;

  const runNeighborPrefetch = useCallback(() => {
    const photos = effectivePhotosRef.current;
    if (!photos || photos.length <= 1) return;
    const idx0 = activeIndexRef.current;
    for (const offset of [-2, 2]) {
      const idx = idx0 + offset;
      if (idx < 0 || idx >= photos.length) continue;
      const u = photos[idx]?.FullSizeUrl;
      if (!u || prefetchedUrlsRef.current.includes(u)) continue;
      Image.prefetch(u, 'disk').catch(() => {});
      prefetchedUrlsRef.current.push(u);
      if (prefetchedUrlsRef.current.length > PREFETCH_RING_SIZE) {
        prefetchedUrlsRef.current.splice(0, prefetchedUrlsRef.current.length - PREFETCH_RING_SIZE);
      }
    }
  }, []);

  // The active (centered) photo just painted → prefetch neighbors now that they
  // no longer compete with the visible image. Neighbor-pane settles (uri !==
  // active) are ignored. Imperative, no setState (see runNeighborPrefetch).
  const handleImageLoadEnd = useCallback((loadedUri: string) => {
    if (loadedUri === activeUriRef.current) runNeighborPrefetch();
  }, [runNeighborPrefetch]);

  // PostCard / ProfileScreen single-photo opens pass a smaller URI (e.g.
  // post.PhotoUrl, member.PhotoUrl) and only learn the high-res FullSizeUrl
  // once the async PhotosService.getPhotoDetails fetch resolves. If we let
  // the carousel pane swap straight to FullSizeUrl on arrival, the pane's
  // <Image> source change triggers a fresh network fetch — visible as a
  // black "disappear, then reappear" flash on slow connections after the
  // morph has already faded out.
  //
  // Instead: prefetch the new FullSizeUrl into expo-image's disk cache, and
  // ONLY then bump activeUri. effectivePhotos derives the pane URI from
  // activeUri (see the synthesized Photo above), so the swap becomes a
  // cache-hit decode — gap-free. We also guard against stale fetches with
  // a `cancelled` flag so a hide / re-open doesn't race-set a now-irrelevant
  // URI.
  useEffect(() => {
    if (!visible) return;
    const fullSize = resolvedPhoto?.FullSizeUrl;
    if (!fullSize) return;
    if (fullSize === activeUri) return;
    let cancelled = false;
    Image.prefetch(fullSize, 'disk').finally(() => {
      if (cancelled) return;
      setActiveUri(fullSize);
    });
    return () => {
      cancelled = true;
    };
  }, [visible, resolvedPhoto?.FullSizeUrl, activeUri]);

  // Preload activeIndex ± 2 only — the ±1 neighbors are already mounted by the
  // carousel panes themselves (Image.prefetch on those URLs would just race
  // with the pane <Image>s for bandwidth). Gated on the active photo having
  // painted (hasUriDisplayed) so it never competes with the visible image's load
  // — important on slow connections. This effect covers navigating to an
  // ALREADY-loaded neighbor; the fresh-load case is handled imperatively by
  // handleImageLoadEnd. Both call the same dedup'd runNeighborPrefetch. Gating
  // on `visible` keeps it from firing on initial mount with the stale
  // activeIndex=0 default.
  useEffect(() => {
    if (!visible) return;
    if (!activeUri || !hasUriDisplayed(activeUri)) return;
    runNeighborPrefetch();
  }, [visible, activeIndex, activeUri, effectivePhotos, runNeighborPrefetch]);

  // Reset all state
  const resetState = useCallback(() => {
    setUiVisible(true);
    setIsClosing(false);
    setIsBottomSheetOpen(false);
    setActiveUri(null);
    setActivePhoto(null);
    setImageLoadError(null);
    setActiveIndex(0);
    setDisplayedBgIndex(0);
    lastBgIndexShared.value = 0;
    setSlotPageIndices([-1, 0, 1]);
    setActiveSlotIndex(1);
    hasPushedHistoryRef.current = false;
    prefetchedUrlsRef.current = [];
    bgUriByIndexRef.current.clear();
    programmaticShiftInFlightRef.current = false;
    SystemBars.setHidden(false);

    // IMPORTANT: do NOT reset visual SVs (carouselOpacity, trackTranslate*,
    // pane transforms) here. resetState runs from the close timeout while the
    // Modal is still playing its 200 ms exit fade — restoring those to their
    // "open" defaults (carouselOpacity=1, translateY=0) would un-hide the
    // carousel and snap the dragged pane back to center mid-exit, producing a
    // visible flash. Those SVs stay at their faded close-state and get
    // re-initialized on the next open by the visible-effect (trackTranslateX,
    // paneSlots.activeSlot) and useImageViewerAnimation's open effect
    // (carouselOpacity, trackTranslateY, pane transforms).
    sv.isGestureActive.value = false;
    sv.isPanning.value = false;
  }, [sv]);

  const navigateToProfile = () => {
    if (!resolvedPhoto?.Member?.UserName) return;

    const route = getMemberPath(resolvedPhoto.Member);

    closeViewer();
    if (pathname?.toLowerCase() !== route.toLowerCase()) {
      setTimeout(() => {
        router.push(route as any);
      }, 100);
    }
  };

  // Animation hook handles open morph + close + rotation + bottom-sheet shift.
  const { closeViewer, closeViewerInstant } = useImageViewerAnimation({
    visible,
    uri: activeUri,
    thumbnailLayout,
    isDesktop,
    isLargeScreen,
    isBottomSheetOpen,
    // On Android, hide the chrome the instant a close begins (not when it completes) so the
    // header / caption / floating-actions dark blurs don't linger over the fading backdrop
    // and sample the content behind them (blur flash). On iOS this stays a no-op and the
    // chrome hides on close completion (via onClose) as before.
    onCloseStart: hideChromeOnCloseStart ? () => setIsClosing(true) : undefined,
    onClose: () => {
      setIsClosing(true);
      onClose();
    },
    resetState,
    isClosingProgrammatically,
    sv,
    paneSlots,
    activeIndex,
    paneWidth,
    paneHeight,
  });

  useEffect(() => {
    closeViewerRef.current = closeViewer;
  }, [closeViewer]);

  // Gesture hook: track-mode pan / in-pane zoom-pan / pinch / tap.
  const composedGesture = useImageViewerGestures({
    sv,
    paneSlots,
    isDesktop,
    paneWidth,
    closeViewer,
    closeViewerInstant,
    toggleUiVisibility,
    isCarouselEnabled,
    activeIndex,
    photoCount,
    onCommitShift: commitShift,
    gesturesEnabled: !isClosingDueToNavigation,
  });

  // Web-specific back button handling
  useEffect(() => {
    if (Platform.OS !== 'web' || disableHistoryNavigation) return;

    if (!visible) {
      hasPushedHistoryRef.current = false;
      return;
    }

    if (!hasPushedHistoryRef.current && resolvedPhoto?.Guid) {
      const photoUrl = `/photo/${resolvedPhoto.Guid}`;
      window.history.pushState({ imageViewer: true }, '', photoUrl);
      hasPushedHistoryRef.current = true;

      const appName = community?.name || 'Grokio';
      const photoTitle = t('photos.photoOf', 'Photo of {{userName}}', { userName: resolvedPhoto.Member.UserName });
      document.title = `${appName} - ${photoTitle}`;
    }

    const handlePopState = (_event: PopStateEvent) => {
      if (visible && !isClosingProgrammatically.current) {
        closeViewerRef.current();
      }
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [visible, disableHistoryNavigation, resolvedPhoto?.Guid]);

  // Web-only arrow key navigation
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    if (!effectivePhotos || effectivePhotos.length <= 1) return;
    const handler = (e: KeyboardEvent) => {
      if (!visible) return;
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goPrev();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        goNext();
      }
    };
    window.addEventListener('keydown', handler as any);
    return () => window.removeEventListener('keydown', handler as any);
  }, [visible, effectivePhotos, goPrev, goNext]);

  return (
    <Modal isOpen={visible && (!!activeUri || isPhotoLocked)} onClose={closeViewer} size="full">
      <ModalBackdrop className="bg-black opacity-100" />
      <ModalContent
        className="w-full h-full bg-black p-0 m-0 rounded-none border-0 shadow-none max-w-none max-h-none"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          paddingTop: Platform.OS === 'android' ? 0 : undefined,
        }}
        initial={{
          opacity: 0,
        }}
        animate={{
          opacity: 1,
        }}
        exit={{
          opacity: 0,
        }}
        transition={{
          type: 'timing',
          duration: 200,
        }}
      >
        <BottomSheetModalProvider>
        <View
          className="absolute inset-0 bg-black"
          pointerEvents="box-none"
          onLayout={handleContainerLayout}
          {...(Platform.OS === 'web' && !viewerAllowsImageDownload
            ? ({ onContextMenu: (event: React.MouseEvent) => event.preventDefault() } as any)
            : {})}
        >
          <BlurTargetView ref={blurTargetRef} style={StyleSheet.absoluteFill}>
            {isPhotoLocked && backgroundUri ? (
              <ImageViewerContentLocked
                iconUrl={backgroundUri}
                onClose={closeViewer}
                isCarouselEnabled={isCarouselEnabled}
                onSwipePrev={goPrev}
                onSwipeNext={goNext}
              />
            ) : activeUri ? (
              <>
                {/* Background photo with blur overlay (single instance, fades on URI change) */}
                <Animated.View
                  style={[
                    { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 0 },
                    bgOpacityStyle,
                  ]}
                >
                  <ImageViewerBackground uri={backgroundUri ?? activeUri} />
                </Animated.View>

                {viewerLoadErrorMessage ? (
                  // When the photo details or active image source fails, skip the
                  // carousel and show a centered MessageView with an explicit
                  // close button. The header's X button remains functional too.
                  <View
                    className="absolute inset-0 items-center justify-center z-10 px-6"
                    pointerEvents="box-none"
                  >
                    <View className="bg-black rounded-2xl p-4" pointerEvents="auto">
                      <MessageView
                        type="warning"
                        icon={Alert02Icon}
                        text={t('common.error.general', 'Oops!')}
                        subText={viewerLoadErrorMessage}
                        textClassName="text-white"
                        disableHeight
                      >
                        <ButtonWrapper
                          text={t('common.close', 'Close')}
                          action="primary"
                          onPress={closeViewer}
                          className="mt-4"
                        />
                      </MessageView>
                    </View>
                  </View>
                ) : (
                /* Carousel + gestures */
                <GestureHandlerRootView
                  style={[
                    StyleSheet.absoluteFill,
                    Platform.OS === 'android' && { top: 0, left: 0, right: 0, bottom: 0 },
                  ]}
                  pointerEvents="box-none"
                >
                  <GestureDetector gesture={composedGesture}>
                    <Animated.View style={StyleSheet.absoluteFill} pointerEvents="auto">
                      <ImageViewerCarousel
                        photos={effectivePhotos}
                        slotPageIndices={slotPageIndices}
                        activeSlotIndex={activeSlotIndex}
                        paneWidth={paneWidth}
                        paneHeight={paneHeight}
                        isDesktop={isDesktop}
                        trackTranslateX={sv.trackTranslateX}
                        trackTranslateY={sv.trackTranslateY}
                        carouselOpacity={sv.carouselOpacity}
                        isPanning={sv.isPanning}
                        paneSlots={paneSlots}
                        allowImageDownload={viewerAllowsImageDownload}
                        onImageLoadError={handleImageLoadError}
                        onImageLoadEnd={handleImageLoadEnd}
                      />
                      {/* Morph overlay: kept mounted whenever the viewer has an
                          active URI, sitting invisibly (animOpacity=0) on top of
                          the carousel during steady-state browsing. Mounting it
                          permanently — instead of toggling on the open and close
                          animations — eliminates the 1-frame mount gap that used
                          to flash the carousel through during the close handoff.
                          Cost is one extra decoded copy of the active image
                          (already in expo-image's disk cache). */}
                      {activeUri ? (
                        <ImageViewerContent
                          uri={activeUri}
                          animLeft={sv.animLeft}
                          animTop={sv.animTop}
                          animWidth={sv.animWidth}
                          animHeight={sv.animHeight}
                          animOpacity={sv.animOpacity}
                        />
                      ) : null}
                    </Animated.View>
                  </GestureDetector>
                </GestureHandlerRootView>
                )}
              </>
            ) : null}
          </BlurTargetView>
          {/* Header (profile and close) - positioned above everything */}
          <ImageViewerHeader
            photo={resolvedPhoto ?? undefined}
            onClose={closeViewer}
            onNavigateToProfile={navigateToProfile}
            isClosing={isClosing}
            uiVisible={chromeVisible}
            onBottomSheetOpenChange={setIsBottomSheetOpen}
            downloadSourceUri={activeUri}
            allowImageDownload={viewerAllowsImageDownload}
            disableSocialActions={disableSocialActions}
            blurTarget={blurTargetRef}
          />
          {/* Caption (hidden for locked photos). Sits at bottom: 0 with its
              dark blur card extending all the way to the bottom edge — visually
              flowing BEHIND the floating likes/comments overlay (z-20 here vs
              z-30 on the overlay). The bottom sheet reserves a fixed amount of
              bottom padding internally (LIKES_OVERLAY_CAPTION_RESERVED_SPACE)
              so caption text never hides behind the overlay. */}
          {!isPhotoLocked && !viewerLoadErrorMessage && !disableSocialActions && (
            <ImageViewerBottomSheet
              photo={resolvedPhoto ?? undefined}
              uiVisible={chromeVisible}
              isClosing={isClosing}
              blurTarget={blurTargetRef}
            />
          )}

          {/* Floating likes/comments overlay (small screens only) — kept mounted so
              ContextSheets survive chrome hide. Pinned to a FIXED bottom
              position (insets.bottom + 16) so the overlay doesn't jump when
              navigating between photos with and without captions. The caption
              sheet hard-codes a matching bottom clearance — see
              LIKES_OVERLAY_CAPTION_RESERVED_SPACE in ImageViewerConstants. */}
          {!isLargeScreen && resolvedPhoto && !disableSocialActions ? (
            <View className="absolute inset-0 z-30" pointerEvents={chromeVisible && !isClosing ? 'box-none' : 'none'} style={{ opacity: chromeVisible && !isClosing ? 1 : 0 }}>
              <HStack
                className="absolute left-0 right-0 items-center justify-center"
                style={{ bottom: insets.bottom + 16 }}
              >
                <SafeBlurView
                  blurTarget={blurTargetRef}
                  intensity={80}
                  tint="dark"
                  className="rounded-full overflow-hidden">
                  <LikesCommentsOverlay
                    photo={resolvedPhoto}
                    iconSize={28}
                    commentIconSize={26}
                    textSize="lg"
                    iconClassName="text-white"
                    disabled={isPhotoLocked}
                    onBottomSheetOpenChange={setIsBottomSheetOpen}
                  />
                </SafeBlurView>
              </HStack>
            </View>
          ) : null}

          {/* Carousel side arrows (side-only touch zones) */}
          {isCarouselEnabled && chromeVisible && !isClosing ? (
            <CarouselSideArrows
              onPrev={goPrev}
              onNext={goNext}
              canPrev={!!canNavigatePrev}
              canNext={!!canNavigateNext}
              blurTarget={blurTargetRef}
            />
          ) : null}
        </View>
        </BottomSheetModalProvider>
      </ModalContent>
    </Modal>
  );
};
